import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createSkill,
  deleteSkill,
  deleteSkillDraft,
  discardSkillDraftVersion,
  getSkill,
  getSkillChatHistory,
  getSkillDraft,
  getSkillVersions,
  listSkillDrafts,
  listSkills,
  publishSkillVersion,
  restoreSkillDraftVersion,
  revertSkillToVersion,
  saveSkillDraftVersion,
  updateSkill,
} from '@/skillsBuilder/api';
import { extractApiErrorMessage } from '@/skillsBuilder/utilities';
import { errorToast } from '@/utilities/toast.utilities';
import type { PublishVersionRequest, SaveDraftVersionRequest, SkillProject } from '@/skillsBuilder/skillsBuilder.types';

// Query key constants
export const SKILLS_QUERY_KEYS = {
  all: ['skills'] as const,
  detail: (skillId: string) => ['skills', skillId] as const,
  chat: (skillId: string) => ['skills', skillId, 'chat'] as const,
  noChat: ['skills', 'no-chat'] as const,
  newSkill: ['skills', 'new'] as const,
  drafts: ['skill-drafts'] as const,
  draft: ['skill-draft'] as const,
  draftByChatId: (chatId: string) => ['skill-draft', chatId] as const,
  draftNone: ['skill-draft', 'none'] as const,
  versions: (skillId: string) => ['skill-versions', skillId] as const,
  versionsNone: ['skill-versions', 'none'] as const,
};

// === Queries ===

/** Fetch all skills for the dashboard */
export const useSkillsQuery = () =>
  useQuery({
    queryKey: SKILLS_QUERY_KEYS.all,
    queryFn: listSkills,
  });

/** Fetch all skill drafts for the dashboard */
export const useSkillDraftsQuery = () =>
  useQuery({
    queryKey: SKILLS_QUERY_KEYS.drafts,
    queryFn: listSkillDrafts,
  });

/** Fetch a single skill by ID (for editing). Disabled for new skill creation. */
export const useSkillQuery = (skillId: string | undefined) =>
  useQuery({
    queryKey: skillId ? SKILLS_QUERY_KEYS.detail(skillId) : SKILLS_QUERY_KEYS.newSkill,
    queryFn: () => getSkill(skillId!),
    enabled: !!skillId,
  });

/** Fetch a saved draft by chat ID (for restoring after page refresh) */
export const useSkillDraftQuery = (chatId: string | null) =>
  useQuery({
    queryKey: chatId ? SKILLS_QUERY_KEYS.draftByChatId(chatId) : SKILLS_QUERY_KEYS.draftNone,
    queryFn: () => getSkillDraft(chatId!),
    enabled: !!chatId,
  });

/** Fetch chat history for an existing skill (for restoring AI Assistant conversation) */
export const useSkillChatHistoryQuery = (skillId: string | undefined) =>
  useQuery({
    queryKey: skillId ? SKILLS_QUERY_KEYS.chat(skillId) : SKILLS_QUERY_KEYS.noChat,
    queryFn: () => getSkillChatHistory(skillId!),
    enabled: !!skillId,
    refetchOnMount: 'always', // Always fetch fresh on navigation, not cached data
    staleTime: 0,
  });

/**
 * Subscribe to SSE-pushed skill draft updates.
 * Data is pushed via skillsQueryClient.setQueryData() from streaming.utilities.ts.
 * This query never fetches on its own — it only reads from the cache.
 */
export const useSkillDraftFromSSE = () =>
  useQuery<SkillProject | null>({
    queryKey: SKILLS_QUERY_KEYS.draft,
    queryFn: () => null,
    enabled: false,
    staleTime: Infinity,
    gcTime: 5_000, // Keep cache briefly to prevent flicker on quick remounts
  });

// === Mutations ===

export const useCreateSkillMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createSkill,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SKILLS_QUERY_KEYS.all });
      void queryClient.invalidateQueries({ queryKey: SKILLS_QUERY_KEYS.drafts });
    },
  });
};

export const useUpdateSkillMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ skillId, request }: { skillId: string; request: Parameters<typeof updateSkill>[1] }) =>
      updateSkill(skillId, request),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: SKILLS_QUERY_KEYS.all });
      void queryClient.invalidateQueries({ queryKey: SKILLS_QUERY_KEYS.detail(variables.skillId) });
    },
  });
};

export const useDeleteSkillMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteSkill,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SKILLS_QUERY_KEYS.all });
    },
    onError: (error) => {
      errorToast({
        messageKey: 'SKILLS_BUILDER.DELETE_ERROR',
        messageParams: { error: extractApiErrorMessage(error) },
      });
    },
  });
};

export const useDeleteSkillDraftMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteSkillDraft,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SKILLS_QUERY_KEYS.drafts });
    },
    onError: (error) => {
      errorToast({
        messageKey: 'SKILLS_BUILDER.DELETE_DRAFT_ERROR',
        messageParams: { error: extractApiErrorMessage(error) },
      });
    },
  });
};

// === Versioning ===

/** Fetch the published versions + draft history for a skill. */
export const useSkillVersionsQuery = (skillId: string | undefined) =>
  useQuery({
    queryKey: skillId ? SKILLS_QUERY_KEYS.versions(skillId) : SKILLS_QUERY_KEYS.versionsNone,
    queryFn: () => getSkillVersions(skillId!),
    enabled: !!skillId,
  });

const invalidateVersions = (queryClient: ReturnType<typeof useQueryClient>, skillId: string) => {
  void queryClient.invalidateQueries({ queryKey: SKILLS_QUERY_KEYS.versions(skillId) });
};

// Version operations that change the DLF working tree (revert, restore, discard,
// publish) also need to invalidate the skill detail so the editor refetches the
// new file contents from the source of truth.
const invalidateVersionsAndContent = (queryClient: ReturnType<typeof useQueryClient>, skillId: string) => {
  invalidateVersions(queryClient, skillId);
  void queryClient.invalidateQueries({ queryKey: SKILLS_QUERY_KEYS.detail(skillId) });
};

export const usePublishSkillVersionMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ skillId, request }: { skillId: string; request: PublishVersionRequest }) =>
      publishSkillVersion(skillId, request),
    onSuccess: (_data, { skillId }) => invalidateVersionsAndContent(queryClient, skillId),
  });
};

export const useSaveSkillDraftVersionMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ skillId, request }: { skillId: string; request: SaveDraftVersionRequest }) =>
      saveSkillDraftVersion(skillId, request),
    // Save draft just commits the current working tree — content didn't change,
    // only history did.
    onSuccess: (_data, { skillId }) => invalidateVersions(queryClient, skillId),
  });
};

export const useRestoreSkillDraftVersionMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ skillId, sha }: { skillId: string; sha: string }) => restoreSkillDraftVersion(skillId, sha),
    onSuccess: (_data, { skillId }) => invalidateVersionsAndContent(queryClient, skillId),
  });
};

export const useDiscardSkillDraftVersionMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ skillId }: { skillId: string }) => discardSkillDraftVersion(skillId),
    onSuccess: (_data, { skillId }) => invalidateVersionsAndContent(queryClient, skillId),
  });
};

export const useRevertSkillToVersionMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ skillId, versionLabel }: { skillId: string; versionLabel: string }) =>
      revertSkillToVersion(skillId, versionLabel),
    onSuccess: (_data, { skillId }) => invalidateVersionsAndContent(queryClient, skillId),
  });
};

// === SSE Integration ===

/**
 * Hook that returns a function to push SSE skill draft updates into the React Query cache.
 * Uses the QueryClient provided by ApiProvider.
 */
export const usePushSkillDraftUpdate = () => {
  const queryClient = useQueryClient();
  return (draft: SkillProject) => {
    queryClient.setQueryData(SKILLS_QUERY_KEYS.draft, draft);
  };
};
